import axios from "axios";
import { set } from "date-fns";
import { array, string } from "decoders";
import { dashboardDataDecoder } from "./dashboardData";
import { printDataDecoder } from "./printData";
import { wellLevelsDataDecoder } from "./wellLevelsData";
import { Summary, summaryDecoder } from "./summaryData";

export type ProjectID = "surfaceWater" | "groundWater" | "drinkingWater";

export const server = axios.create({ baseURL: `${process.env.REACT_APP_SERVER_URL}/api` });

export async function getSites(project: ProjectID) {
  const { data } = await server.get(`${project}/sites`);
  return array(string).verify(data);
}

export async function getAnalytes(
  project: ProjectID,
  sites: string[],
  startDate: Date,
  endDate: Date,
  isDetected: boolean,
) {
  const { data } = await server.post(`${project}/analytes`, { sites, startDate, endDate, isDetected });
  return array(string).verify(data);
}

export async function GetBarGraphData(
  project: ProjectID,
  analyte: string,
  sites: string[],
  startDate: Date,
  endDate: Date,
) {
  const { data } = await server.post(`${project}/dashboardData`, {
    analyte,
    sites,
    startDate: set(startDate, { hours: 0, minutes: 0, seconds: 0 }),
    endDate: set(endDate, { hours: 23, minutes: 59, seconds: 59 }),
  });

  return dashboardDataDecoder.verify(data);
}

export async function GetWellLevelsData(startDate: Date, endDate: Date, sites: string[]) {
  const { data } = await server.post(`groundWater/wellLevelsData`, {
    startDate,
    endDate,
    sites: sites.map((site) => site.toUpperCase()),
  });

  return wellLevelsDataDecoder.verify(data);
}

export async function CreateDashboardPrintData(
  project: ProjectID,
  analyte: string,
  sites: string[],
  startDate: Date,
  endDate: Date,
) {
  const { data } = await server.post(`${project}/dashboardData/print`, {
    analyte,
    sites,
    startDate: set(startDate, { hours: 0, minutes: 0, seconds: 0 }),
    endDate: set(endDate, { hours: 23, minutes: 59, seconds: 59 }),
  });

  return data;
}

export async function GetPrintData(id: string) {
  const { data } = await server.get(`print/${id}`);
  return printDataDecoder.verify(data);
}

export async function CreateWellLevelsPrintData(startDate: Date, endDate: Date, sites: string[]) {
  const { data } = await server.post(`groundWater/wellLevelsData/print`, {
    startDate: startDate,
    endDate: endDate,
    sites: sites,
  });

  return data;
}

export async function GetSummaryData(project: ProjectID): Promise<Summary> {
  const { data } = await server.get(`${project}/summary`);

  return summaryDecoder.verify(data);
}

export function projectToTitle(project: ProjectID) {
  switch (project) {
    case "groundWater":
      return "Ground Water";
    case "surfaceWater":
      return "Surface Water";
    case "drinkingWater":
      return "Drinking Water";
  }
}
